// ⚠️ 자동 생성 파일 — 직접 수정 금지. 정본은 dentcluster-agent/shared/patient-name.js
//    수정 절차: 정본 수정 → node scripts/sync-shared.js (release-all.sh가 불일치 시 빌드 중단)
// [공통 정본] 환자명 정제 — CAD 파일 PatientName에 섞인 쉐이드/재료/메모 표기를 이름과 분리
// 원칙: "분리"는 공통 적용(이름에 숫자·재료가 붙을 일은 없음),
//       "해석"은 보수적으로(확실한 표기만 필드 후보로, 단독 숫자는 의미 단정 없이 메모 보존).
//
// ★ 이 파일이 정본(shared/). 직접 쓰는 곳은 없고 사본이 배포된다:
//   - 에이전트: electron/shared/patient-name.js (CJS 그대로)
//   - 웹: src/utils/shared/patient-name.js (ESM 변환)
//   수정 후 반드시 `node scripts/sync-shared.js` 실행 (release-all.sh가 불일치 시 중단).
// NFC 통일(#22, 2026-08-06): 맥 파일명(NFD)·웹 입력(NFC) 매칭 어긋남 예방 —
//   정규화 앞단에서 normalize('NFC') 적용. (dedupId 키는 normPatient(원본) 기준이라 영향 없음)

const MATERIALS = [
    ['지르코니아', '지르코니아'], ['zirconia', '지르코니아'], ['지르', '지르코니아'], ['zir', '지르코니아'],
    ['이맥스', 'e-max'], ['e-max', 'e-max'], ['emax', 'e-max'],
    ['레진', '레진'], ['resin', '레진'],
    ['골드', '골드'], ['gold', '골드'], ['pmma', 'PMMA'], ['pfm', 'PFM'],
    ['메탈', '메탈'], ['metal', '메탈'], ['왁스', '왁스'], ['wax', '왁스'],
];
const PROSTHESIS = [
    ['어버트먼트', '어버트먼트'], ['abutment', '어버트먼트'],
    ['라미네이트', '라미네이트'], ['비니어', '라미네이트'], ['veneer', '라미네이트'],
    ['인레이', '인레이'], ['inlay', '인레이'], ['온레이', '온레이'], ['onlay', '온레이'],
    ['크라운', '크라운'], ['crown', '크라운'], ['브릿지', '브릿지'], ['bridge', '브릿지'],
    ['rinlay', '레진인레이'], ['rin', '레진인레이'],   // 현장 축약 표기
    ['코핑', '코핑'], ['coping', '코핑'], ['임시치', '임시치'], ['임시', '임시치'],
    ['temp', '임시치'], ['tem', '임시치'], ['덴쳐', '덴쳐'], ['denture', '덴쳐'],
    ['포스트', '포스트'], ['post', '포스트'],
];
const MEMOS = [
    '카톡포토참고', '기존보철참고', '의뢰서확인', '포토확인', '포토참고', '포토메일',
    '바이트스캔', '내면처리', '내면오펙', '이름적기', '쉐이드', 'shade', '바이트',
    '포토', 'photo', '참고', '참조', '확인', '첨부', '오펙', '스캔',
];

// 코어(한글 이름) 끝에서 떼어낼 접미 후보 — 긴 것부터
const KO_SUFFIX = [
    ...MATERIALS.map(([k, v]) => ({ k, v, kind: 'material' })),
    ...PROSTHESIS.map(([k, v]) => ({ k, v, kind: 'prosthesis' })),
    ...MEMOS.map(k => ({ k, v: k, kind: 'note' })),
].filter(({ k }) => /[가-힣]/.test(k)).sort((a, b) => b.k.length - a.k.length);

const MAT_SORTED = [...MATERIALS].sort((a, b) => b[0].length - a[0].length);
const PRO_SORTED = [...PROSTHESIS].sort((a, b) => b[0].length - a[0].length);
const MEMO_SORTED = [...MEMOS].sort((a, b) => b.length - a.length);

// 확실한 쉐이드 표기만: A3 / b2 / 3.5 / A3.5 / 2~3 / 2.5~3 — 단독 정수(2, 3)는 제외
const SHADE_CERTAIN = /^(?:[A-Da-d]\d(?:[.,]\d)?|\d[.,]\d|[A-Da-d]?\d(?:[.,]\d)?~[A-Da-d]?\d(?:[.,]\d)?)$/;

function toHalfWidth(s) {
    return s.replace(/[０-９Ａ-Ｚａ-ｚ．～]/g, (c) =>
        c === '．' ? '.' : c === '～' ? '~' : String.fromCharCode(c.charCodeAt(0) - 0xFEE0));
}

/** 토큰 하나를 분해·분류해 out에 누적 */
function classifyToken(tk, out) {
    let rest = tk, guard = 0;
    while (rest && guard++ < 12) {
        const lower = rest.toLowerCase();
        const memo = MEMO_SORTED.find(k => lower.startsWith(k));
        const mat = MAT_SORTED.find(([k]) => lower.startsWith(k));
        const pro = PRO_SORTED.find(([k]) => lower.startsWith(k));
        const shade = rest.match(/^[A-Da-d]?\d(?:[.,]\d)?(?:~[A-Da-d]?\d(?:[.,]\d)?)?/);
        if (memo) { out.notes.push(memo); rest = rest.slice(memo.length); }
        else if (mat) { out.materials.push(mat[1]); rest = rest.slice(mat[0].length); }
        else if (pro) { out.prosthesis.push(pro[1]); rest = rest.slice(pro[0].length); }
        else if (shade && shade[0] && SHADE_CERTAIN.test(shade[0])) { out.shades.push(shade[0]); rest = rest.slice(shade[0].length); }
        else if (/^\d/.test(rest)) { const n = rest.match(/^\d+/)[0]; out.notes.push(n); rest = rest.slice(n.length); }
        else { out.notes.push(rest); break; }
    }
}

/**
 * 환자명 정제.
 * @returns {{ name, raw, shade, material, prosthesis, note, changed }}
 *  - name: 정제된 이름 (실패 시 원본 그대로)
 *  - shade/material/prosthesis: 확실하게 해석된 표기만 (없으면 '')
 *  - note: 의미 단정 없이 보존하는 나머지 표기 (단독 숫자 포함)
 */
function cleanPatientName(raw) {
    // NFC 통일: 맥 파일명 유래 한글(NFD 자모 분해형)을 완성형으로 — 이후 한글 정규식·사전 매칭의 전제
    const original = (raw == null ? '' : String(raw)).normalize('NFC').trim();
    const empty = { name: original, raw: original, shade: '', material: '', prosthesis: '', note: '', changed: false };
    if (!original) return empty;
    let s = toHalfWidth(original).replace(/\s+/g, ' ');
    // "유 이 현"처럼 한글 사이 공백은 결합 (뒤이어 접미 사전으로 다시 분리됨)
    s = s.split(' ').reduce((acc, part) =>
        acc && /[가-힣]$/.test(acc) && /^[가-힣]/.test(part) ? acc + part : (acc ? acc + ' ' + part : part), '');

    const runs = s.match(/[가-힣]+/g);
    if (!runs) return empty;   // 한글 없음(영문 이름 등) — 건드리지 않음

    const out = { shades: [], materials: [], prosthesis: [], notes: [] };

    // 이름 코어: 첫 한글 구간에서 접미 사전을 떼어냄 (남는 이름이 2자 이상일 때만)
    const stripSuffix = (run) => {
        let core = run, stripped = true;
        while (stripped) {
            stripped = false;
            for (const { k, v, kind } of KO_SUFFIX) {
                if (core.length - k.length >= 2 && core.toLowerCase().endsWith(k)) {
                    core = core.slice(0, -k.length);
                    if (kind === 'material') out.materials.push(v);
                    else if (kind === 'prosthesis') out.prosthesis.push(v);
                    else out.notes.push(v);
                    stripped = true;
                    break;
                }
            }
        }
        return core;
    };

    let core = stripSuffix(runs[0]);
    let coreSource = runs[0];
    // 첫 구간이 1자(성씨 조각 등)이고 뒤에 온전한 한글 구간이 있으면 그쪽을 이름으로
    if (core.length < 2 && runs.length > 1) {
        for (const r of runs.slice(1)) {
            const c = stripSuffix(r);
            if (c.length >= 2) { out.notes.push(core); core = c; coreSource = r; break; }
        }
    }
    if (core.length < 2) return empty;   // 안전장치: 이름이 못 되면 원본 유지

    // 코어를 제외한 나머지 문자열 → 토큰 분류
    const idx = s.indexOf(coreSource);
    const rest = (s.slice(0, idx) + ' ' + s.slice(idx + coreSource.length)).trim();
    for (const tk of rest.split(/[\s+_,\-/()]+/).filter(Boolean)) {
        if (tk === coreSource) continue;
        classifyToken(tk, out);
    }

    return {
        name: core,
        raw: original,
        shade: out.shades[0] || '',
        material: out.materials[0] || '',
        prosthesis: out.prosthesis[0] || '',
        note: [...new Set([...out.materials.slice(1), ...out.prosthesis.slice(1), ...out.shades.slice(1), ...out.notes])].join(' '),
        changed: core !== original,
    };
}

/** parsedCases/workOrders 문서용 표시 헬퍼 — 에이전트가 이미 정제한 문서(patientNameRaw 보유)는 그대로,
 *  구버전 문서는 즉석 정제. 반환: { name, raw } (raw는 정제 전 원본, 동일하면 name과 같음) */
function displayPatientName(docLike) {
    if (!docLike) return { name: '', raw: '' };
    if (docLike.patientNameRaw !== undefined) {
        return { name: docLike.patientName || '', raw: docLike.patientNameRaw || docLike.patientName || '' };
    }
    const c = cleanPatientName(docLike.patientName);
    return { name: c.name, raw: c.raw };
}

export { cleanPatientName, displayPatientName };